// ============================================================================
// Unified Flows Page
// Combines dynamic-flows, flow-builder, flow-tester, and native-flows
// into a single page with flow list + tabbed detail view.
// ============================================================================

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api';
import {
  Plus,
  GitBranch,
  Circle,
  MessageCircle,
  AlertCircle,
  Copy,
  ArrowLeft,
  Pencil,
  Eye,
  Play,
  Upload,
  ChevronRight,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { FlowEditTab } from '@/components/operator/flow-tabs/FlowEditTab';
import { FlowVisualTab } from '@/components/operator/flow-tabs/FlowVisualTab';
import { FlowTestTab } from '@/components/operator/flow-tabs/FlowTestTab';
import { FlowPublishTab } from '@/components/operator/flow-tabs/FlowPublishTab';
import type {
  FlowSummary,
  FlowDetail,
  EditableNode,
  FlowTemplate,
} from '@/components/operator/flow-tabs/types';

// ============================================================================
// Constants
// ============================================================================

const FLOW_TEMPLATES: FlowTemplate[] = [
  {
    id: 'booking',
    nameKey: 'dynamicFlows.templates.booking.name',
    descriptionKey: 'dynamicFlows.templates.booking.description',
    nodes: [
      { slug: 'greeting', name: 'Greeting', namePtBr: 'Saudacao', nodeType: 'start', messageText: 'Welcome! How can I help you today?', messageTextPtBr: 'Bem-vindo! Como posso ajudar voce hoje?', _isNew: true },
      { slug: 'select-service', name: 'Select Service', namePtBr: 'Selecionar Servico', nodeType: 'interaction', messageText: 'Please select a service:', messageTextPtBr: 'Por favor, selecione um servico:', _isNew: true },
      { slug: 'select-date', name: 'Select Date', namePtBr: 'Selecionar Data', nodeType: 'interaction', messageText: 'Choose your preferred date:', messageTextPtBr: 'Escolha sua data preferida:', _isNew: true },
      { slug: 'select-time', name: 'Select Time', namePtBr: 'Selecionar Horario', nodeType: 'interaction', messageText: 'Select a time slot:', messageTextPtBr: 'Selecione um horario:', _isNew: true },
      { slug: 'confirm', name: 'Confirm Booking', namePtBr: 'Confirmar Agendamento', nodeType: 'decision', messageText: 'Please confirm your booking details:', messageTextPtBr: 'Por favor, confirme os detalhes do agendamento:', _isNew: true },
      { slug: 'success', name: 'Booking Confirmed', namePtBr: 'Agendamento Confirmado', nodeType: 'terminal', messageText: 'Your booking is confirmed! See you soon.', messageTextPtBr: 'Seu agendamento esta confirmado! Ate logo.', _isNew: true },
    ],
  },
  {
    id: 'reschedule',
    nameKey: 'dynamicFlows.templates.reschedule.name',
    descriptionKey: 'dynamicFlows.templates.reschedule.description',
    nodes: [
      { slug: 'start', name: 'Start', namePtBr: 'Inicio', nodeType: 'start', messageText: 'Need to reschedule? Let me help.', messageTextPtBr: 'Precisa remarcar? Deixe-me ajudar.', _isNew: true },
      { slug: 'find-booking', name: 'Find Booking', namePtBr: 'Encontrar Agendamento', nodeType: 'action', messageText: 'Looking up your current booking...', messageTextPtBr: 'Buscando seu agendamento atual...', _isNew: true },
      { slug: 'new-date', name: 'New Date', namePtBr: 'Nova Data', nodeType: 'interaction', messageText: 'Select a new date:', messageTextPtBr: 'Selecione uma nova data:', _isNew: true },
      { slug: 'new-time', name: 'New Time', namePtBr: 'Novo Horario', nodeType: 'interaction', messageText: 'Select a new time:', messageTextPtBr: 'Selecione um novo horario:', _isNew: true },
      { slug: 'confirm-reschedule', name: 'Confirm', namePtBr: 'Confirmar', nodeType: 'decision', messageText: 'Confirm your new appointment:', messageTextPtBr: 'Confirme seu novo agendamento:', _isNew: true },
      { slug: 'done', name: 'Done', namePtBr: 'Concluido', nodeType: 'terminal', messageText: 'Rescheduled successfully!', messageTextPtBr: 'Remarcado com sucesso!', _isNew: true },
    ],
  },
  {
    id: 'reminder',
    nameKey: 'dynamicFlows.templates.reminder.name',
    descriptionKey: 'dynamicFlows.templates.reminder.description',
    nodes: [
      { slug: 'send-reminder', name: 'Send Reminder', namePtBr: 'Enviar Lembrete', nodeType: 'start', messageText: 'Reminder: You have an appointment tomorrow.', messageTextPtBr: 'Lembrete: Voce tem um agendamento amanha.', _isNew: true },
      { slug: 'wait-response', name: 'Wait Response', namePtBr: 'Aguardar Resposta', nodeType: 'interaction', messageText: 'Reply CONFIRM to confirm or RESCHEDULE to change.', messageTextPtBr: 'Responda CONFIRMAR para confirmar ou REMARCAR para alterar.', _isNew: true },
      { slug: 'check-response', name: 'Check Response', namePtBr: 'Verificar Resposta', nodeType: 'decision', messageText: 'Processing your response...', messageTextPtBr: 'Processando sua resposta...', _isNew: true },
      { slug: 'confirmed', name: 'Confirmed', namePtBr: 'Confirmado', nodeType: 'terminal', messageText: 'Great! See you tomorrow.', messageTextPtBr: 'Otimo! Ate amanha.', _isNew: true },
    ],
  },
];

// ============================================================================
// Helpers
// ============================================================================

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

// ============================================================================
// API Helpers
// ============================================================================

const flowsApi = {
  async list(businessId: string): Promise<FlowSummary[]> {
    const res = await apiFetch(
      `/api/operator/flows?businessId=${encodeURIComponent(businessId)}`,
    );
    if (!res.ok) throw new Error('Failed to load flows');
    const json = await res.json();
    return json.flows || [];
  },

  async create(
    businessId: string,
    data: {
      name: string;
      namePtBr: string;
      slug: string;
      description: string;
      descriptionPtBr: string;
      nodes?: Omit<EditableNode, 'id' | '_isNew'>[];
    },
  ): Promise<FlowDetail> {
    const res = await apiFetch(
      `/api/operator/flows?businessId=${encodeURIComponent(businessId)}`,
      { method: 'POST', body: JSON.stringify(data) },
    );
    if (!res.ok) throw new Error('Failed to create flow');
    const json = await res.json();
    return json.flow;
  },

  async update(
    flowSlug: string,
    businessId: string,
    data: { isActive?: boolean },
  ): Promise<FlowDetail> {
    const res = await apiFetch(
      `/api/operator/flows/${encodeURIComponent(flowSlug)}?businessId=${encodeURIComponent(businessId)}`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
    if (!res.ok) throw new Error('Failed to update flow');
    const json = await res.json();
    return json.flow;
  },
};

// ============================================================================
// CreateFlowDialog
// ============================================================================

function CreateFlowDialog({
  open,
  onOpenChange,
  businessId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [namePtBr, setNamePtBr] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionPtBr, setDescriptionPtBr] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  useEffect(() => {
    if (!slugManuallyEdited && name) {
      setSlug(slugify(name));
    }
  }, [name, slugManuallyEdited]);

  useEffect(() => {
    if (open) {
      setName('');
      setNamePtBr('');
      setDescription('');
      setDescriptionPtBr('');
      setSlug('');
      setSlugManuallyEdited(false);
      setError(null);
    }
  }, [open]);

  const handleCreate = useCallback(async () => {
    if (!name.trim() || !slug.trim()) return;
    setSaving(true);
    setError(null);

    try {
      await flowsApi.create(businessId, {
        name: name.trim(),
        namePtBr: namePtBr.trim() || name.trim(),
        slug: slug.trim(),
        description: description.trim(),
        descriptionPtBr: descriptionPtBr.trim() || description.trim(),
      });
      onCreated();
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('dynamicFlows.errors.createFailed'),
      );
    } finally {
      setSaving(false);
    }
  }, [name, namePtBr, slug, description, descriptionPtBr, businessId, onCreated, onOpenChange, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('dynamicFlows.createDialog.title')}</DialogTitle>
          <DialogDescription>{t('dynamicFlows.createDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="flow-name">{t('dynamicFlows.createDialog.nameEn')}</Label>
              <Input
                id="flow-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('dynamicFlows.createDialog.namePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="flow-name-pt">{t('dynamicFlows.createDialog.namePtBr')}</Label>
              <Input
                id="flow-name-pt"
                value={namePtBr}
                onChange={(e) => setNamePtBr(e.target.value)}
                placeholder={t('dynamicFlows.createDialog.namePtBrPlaceholder')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="flow-slug">{t('dynamicFlows.createDialog.slug')}</Label>
            <Input
              id="flow-slug"
              value={slug}
              onChange={(e) => {
                setSlug(slugify(e.target.value));
                setSlugManuallyEdited(true);
              }}
              placeholder="e.g. booking-flow"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t('dynamicFlows.createDialog.slugHint')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="flow-desc">{t('dynamicFlows.createDialog.descriptionEn')}</Label>
              <Textarea
                id="flow-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('dynamicFlows.createDialog.descriptionPlaceholder')}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="flow-desc-pt">
                {t('dynamicFlows.createDialog.descriptionPtBr')}
              </Label>
              <Textarea
                id="flow-desc-pt"
                value={descriptionPtBr}
                onChange={(e) => setDescriptionPtBr(e.target.value)}
                placeholder={t('dynamicFlows.createDialog.descriptionPtBrPlaceholder')}
                rows={3}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-4 py-3">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleCreate} disabled={saving || !name.trim() || !slug.trim()}>
            {saving ? t('dynamicFlows.createDialog.creating') : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// FlowCard
// ============================================================================

function FlowCard({
  flow,
  businessId,
  onSelect,
  onToggleActive,
}: {
  flow: FlowSummary;
  businessId: string;
  onSelect: () => void;
  onToggleActive: (isActive: boolean) => void;
}) {
  const { t } = useTranslation();
  const [toggling, setToggling] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(false);
  const [pendingActiveState, setPendingActiveState] = useState(false);

  const handleToggle = useCallback(() => {
    setPendingActiveState(!flow.isActive);
    setConfirmToggle(true);
  }, [flow.isActive]);

  const handleConfirmToggle = useCallback(async () => {
    setToggling(true);
    try {
      await flowsApi.update(flow.slug, businessId, { isActive: pendingActiveState });
      onToggleActive(pendingActiveState);
    } catch {
      // Error handled silently, card will refresh
    } finally {
      setToggling(false);
      setConfirmToggle(false);
    }
  }, [flow.slug, businessId, pendingActiveState, onToggleActive]);

  const formattedDate = useMemo(() => {
    try {
      return new Date(flow.createdAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }, [flow.createdAt]);

  return (
    <>
      <Card
        className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all duration-200 group"
        onClick={onSelect}
        data-testid={`flow-card-${flow.slug}`}
      >
        <div className="p-4 pb-3">
          <div className="flex items-start justify-between">
            <div className="rounded-lg bg-muted p-2 group-hover:bg-primary/10 transition-colors">
              <GitBranch className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={flow.isActive}
                onCheckedChange={handleToggle}
                disabled={toggling}
              />
            </div>
          </div>
        </div>
        <CardContent className="pt-0 space-y-2">
          <p className="text-sm font-medium leading-snug">{flow.name}</p>
          {flow.description && (
            <p className="text-xs text-muted-foreground line-clamp-2">{flow.description}</p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Badge
              variant={flow.isActive ? 'default' : 'secondary'}
              className={
                flow.isActive
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100'
                  : ''
              }
            >
              {flow.isActive ? t('dynamicFlows.active') : t('dynamicFlows.inactive')}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {t('dynamicFlows.nodeCount', { count: flow.steps })}
            </span>
          </div>
          <div className="flex items-center justify-between pt-1">
            {formattedDate && (
              <p className="text-xs text-muted-foreground">{formattedDate}</p>
            )}
            <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
              {t('dynamicFlows.openFlow', 'Open')}
              <ChevronRight className="h-3 w-3" />
            </span>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmToggle} onOpenChange={setConfirmToggle}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingActiveState
                ? t('dynamicFlows.toggleDialog.activateTitle')
                : t('dynamicFlows.toggleDialog.deactivateTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingActiveState
                ? t('dynamicFlows.toggleDialog.activateDescription', { name: flow.name })
                : t('dynamicFlows.toggleDialog.deactivateDescription', { name: flow.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggling}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmToggle} disabled={toggling}>
              {toggling ? t('common.loading') : t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ============================================================================
// PreFillTemplatesSection
// ============================================================================

function PreFillTemplatesSection({
  businessId,
  onCloned,
}: {
  businessId: string;
  onCloned: () => void;
}) {
  const { t } = useTranslation();
  const [cloning, setCloning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClone = useCallback(
    async (template: FlowTemplate) => {
      setCloning(template.id);
      setError(null);

      try {
        const templateNodes = template.nodes.map((n, i) => ({
          ...n,
          position: i,
        }));

        await flowsApi.create(businessId, {
          name: t(template.nameKey),
          namePtBr: t(template.nameKey),
          slug: `${template.id}-${Date.now()}`,
          description: t(template.descriptionKey),
          descriptionPtBr: t(template.descriptionKey),
          nodes: templateNodes.map(({ _isNew, ...rest }) => rest),
        });

        onCloned();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('dynamicFlows.errors.cloneFailed'),
        );
      } finally {
        setCloning(null);
      }
    },
    [businessId, onCloned, t],
  );

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">
        {t('dynamicFlows.prefillTemplates.title')}
      </h3>
      <p className="text-xs text-muted-foreground">
        {t('dynamicFlows.prefillTemplates.description')}
      </p>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-3 py-2">
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {FLOW_TEMPLATES.map((template) => (
          <Card key={template.id} className="hover:border-primary/30 transition-colors">
            <CardContent className="p-4 space-y-2">
              <h4 className="text-sm font-medium">{t(template.nameKey)}</h4>
              <p className="text-xs text-muted-foreground">{t(template.descriptionKey)}</p>
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-muted-foreground">
                  {t('dynamicFlows.nodeCount', { count: template.nodes.length })}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleClone(template)}
                  disabled={cloning === template.id}
                >
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  {cloning === template.id
                    ? t('dynamicFlows.prefillTemplates.cloning')
                    : t('dynamicFlows.prefillTemplates.useTemplate')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// FlowsPage (main component)
// ============================================================================

function FlowsPage() {
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } =
    useOperator();

  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection state
  const [selectedFlow, setSelectedFlow] = useState<FlowSummary | null>(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchFlows = useCallback(async (signal?: AbortSignal) => {
    if (!selectedBusinessId || !operator) return;
    setLoading(true);
    setError(null);

    try {
      const data = await flowsApi.list(selectedBusinessId);
      if (signal?.aborted) return;
      setFlows(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (signal?.aborted) return;
      setError(err instanceof Error ? err.message : t('dynamicFlows.errors.loadFailed'));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [selectedBusinessId, operator, t]);

  useEffect(() => {
    const controller = new AbortController();
    fetchFlows(controller.signal);
    return () => controller.abort();
  }, [fetchFlows]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleFlowSelect = useCallback((flow: FlowSummary) => {
    setSelectedFlow(flow);
  }, []);

  const handleToggleActive = useCallback((flowSlug: string, isActive: boolean) => {
    setFlows((prev) => prev.map((f) => (f.slug === flowSlug ? { ...f, isActive } : f)));
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedFlow(null);
  }, []);

  const handleFlowUpdated = useCallback(() => {
    fetchFlows();
  }, [fetchFlows]);

  const handleFlowCreated = useCallback(() => {
    fetchFlows();
  }, [fetchFlows]);

  // ---------------------------------------------------------------------------
  // Stats
  // ---------------------------------------------------------------------------

  const activeCount = useMemo(() => flows.filter((f) => f.isActive).length, [flows]);
  const totalNodes = useMemo(
    () => flows.reduce((sum, f) => sum + f.steps, 0),
    [flows],
  );

  // ---------------------------------------------------------------------------
  // Loading / Auth guards
  // ---------------------------------------------------------------------------

  if (authLoading) {
    return <LoadingSpinner />;
  }

  if (!operator) return null;

  if (!selectedBusinessId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <GitBranch className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {t('dynamicFlows.selectBusiness')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Flow Detail View (tabbed)
  // ---------------------------------------------------------------------------

  if (selectedFlow) {
    return (
      <div className="p-6 space-y-6" data-testid="flows-detail-view">
        {/* Header with back button */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToList}
            className="flex items-center gap-1"
            data-testid="back-to-flow-list"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('dynamicFlows.backToFlows', 'Back to Flows')}
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight">{selectedFlow.name}</h1>
            {selectedFlow.description && (
              <p className="text-muted-foreground text-sm">{selectedFlow.description}</p>
            )}
          </div>
          <Badge
            variant={selectedFlow.isActive ? 'default' : 'secondary'}
            className={
              selectedFlow.isActive
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : ''
            }
          >
            {selectedFlow.isActive ? t('dynamicFlows.active') : t('dynamicFlows.inactive')}
          </Badge>
        </div>

        {/* Tabbed detail view */}
        <Tabs defaultValue="edit" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="edit" className="flex items-center gap-1.5" data-testid="tab-edit">
              <Pencil className="h-3.5 w-3.5" />
              {t('flows.tabs.edit', 'Edit')}
            </TabsTrigger>
            <TabsTrigger value="visual" className="flex items-center gap-1.5" data-testid="tab-visual">
              <Eye className="h-3.5 w-3.5" />
              {t('flows.tabs.visual', 'Visual')}
            </TabsTrigger>
            <TabsTrigger value="test" className="flex items-center gap-1.5" data-testid="tab-test">
              <Play className="h-3.5 w-3.5" />
              {t('flows.tabs.test', 'Test')}
            </TabsTrigger>
            <TabsTrigger value="publish" className="flex items-center gap-1.5" data-testid="tab-publish">
              <Upload className="h-3.5 w-3.5" />
              {t('flows.tabs.publish', 'Publish')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="edit">
            <FlowEditTab
              flow={selectedFlow}
              flowSlug={selectedFlow.slug}
              businessId={selectedBusinessId}
              onFlowUpdated={handleFlowUpdated}
            />
          </TabsContent>

          <TabsContent value="visual">
            <FlowVisualTab
              flow={selectedFlow}
              flowSlug={selectedFlow.slug}
              businessId={selectedBusinessId}
              onFlowUpdated={handleFlowUpdated}
            />
          </TabsContent>

          <TabsContent value="test">
            <FlowTestTab
              flow={selectedFlow}
              flowSlug={selectedFlow.slug}
              businessId={selectedBusinessId}
              onFlowUpdated={handleFlowUpdated}
            />
          </TabsContent>

          <TabsContent value="publish">
            <FlowPublishTab
              flow={selectedFlow}
              flowSlug={selectedFlow.slug}
              businessId={selectedBusinessId}
              onFlowUpdated={handleFlowUpdated}
            />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Flow List View
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6 space-y-6" data-testid="flows-list-view">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('dynamicFlows.title')}
          </h1>
          <p className="text-muted-foreground">
            {selectedBusiness?.name && `${selectedBusiness.name} - `}
            {t('dynamicFlows.subtitle')}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-flow-button">
          <Plus className="h-4 w-4 mr-2" />
          {t('dynamicFlows.createFlow')}
        </Button>
      </div>

      {/* Stats bar */}
      {!loading && flows.length > 0 && (
        <div className="grid grid-cols-3 gap-4" data-testid="flow-stats">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <GitBranch className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{flows.length}</p>
                <p className="text-xs text-muted-foreground">
                  {t('dynamicFlows.stats.totalFlows')}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-lg bg-green-100 dark:bg-green-900/30 p-2">
                <Circle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeCount}</p>
                <p className="text-xs text-muted-foreground">
                  {t('dynamicFlows.stats.activeFlows')}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-lg bg-blue-100 dark:bg-blue-900/30 p-2">
                <MessageCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalNodes}</p>
                <p className="text-xs text-muted-foreground">
                  {t('dynamicFlows.stats.totalNodes')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error state */}
      {error && (
        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <Button variant="outline" size="sm" className="ml-auto" onClick={() => fetchFlows()}>
              {t('dynamicFlows.retry')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? null : flows.length === 0 ? (
        /* Empty state */
        <div className="space-y-8">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <GitBranch className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">{t('dynamicFlows.noFlows')}</p>
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('dynamicFlows.createFlow')}
              </Button>
            </CardContent>
          </Card>

          <PreFillTemplatesSection
            businessId={selectedBusinessId}
            onCloned={handleFlowCreated}
          />
        </div>
      ) : (
        /* Flow grid + Templates */
        <div className="space-y-8">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            data-testid="flow-grid"
          >
            {flows.map((flow) => (
              <FlowCard
                key={flow.id}
                flow={flow}
                businessId={selectedBusinessId}
                onSelect={() => handleFlowSelect(flow)}
                onToggleActive={(isActive) => handleToggleActive(flow.slug, isActive)}
              />
            ))}
          </div>

          <Separator />

          <PreFillTemplatesSection
            businessId={selectedBusinessId}
            onCloned={handleFlowCreated}
          />
        </div>
      )}

      {/* Create flow dialog */}
      {selectedBusinessId && (
        <CreateFlowDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          businessId={selectedBusinessId}
          onCreated={handleFlowCreated}
        />
      )}
    </div>
  );
}

// ============================================================================
// Route Definition
// ============================================================================

export const Route = createFileRoute('/_auth/operator/flows')({
  component: FlowsPage,
});
